import { Select, TextInput } from '@octano/global-ui';
import { Fragment, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Col, Row } from 'reactstrap';
import { formatOptions } from '../../../utils/selectFormat';
import { parenthoodOptions } from '../../../utils/selectOptions';
import { useAsyncOptionsFieldsLoader } from '../../../views/StudentFileAsyncOptions/AsyncOptionsFieldsLoader';
import { SectionTitle } from '../../text';

type EmergencyContactReformadaSectionProps = {
  prefix: string;
  optionalFields?: string[];
  fieldNames?: {
    isLegalAge: string;
    representativeLegal: string;
    identificationType: string;
    country: string;
    identification: string;
    parenthood: string;
  };
  disabled?: boolean;
};

/**
 * Para usar este componente tu formulario debe estar envuelto en su FormProvider
 * para poder acceder a la información del formulario a través de useFormContext
 * Los nombres agregados en fieldNames se relacionan a los campos que utilizaste
 * al definir tu formulario.
 */
const DynamicFamilyGroupDataReformada = ({
  prefix,
  optionalFields = [],
  fieldNames = {
    isLegalAge: 'personalInformation_isLegalAge',
    representativeLegal: 'familyGroupData_representativeLegal1',
    identificationType: 'familyGroupData_identificationType',
    country: 'familyGroupData_country',
    identification: 'familyGroupData_identification',
    parenthood: 'familyGroupData_parenthood',
  },
  disabled = false,
}: EmergencyContactReformadaSectionProps) => {
  const { t } = useTranslation();
  const { control, watch } = useFormContext();
  const {
    data: { countries, identificationTypes } = {
      data: [],
    },
  } = useAsyncOptionsFieldsLoader();

  const [selectedAge, selectedIdentificationType] = watch([
    fieldNames.isLegalAge,
    fieldNames.identificationType,
  ]);

  // Memo
  const identificationTypeOptions = useMemo(() => {
    return formatOptions(identificationTypes ?? []);
  }, [identificationTypes]);

  const countryOptions = useMemo(() => {
    return formatOptions(countries ?? []);
  }, [countries]);

  const isPassport = useMemo(() => {
    return selectedIdentificationType?.value === 'PASSPORT';
  }, [selectedIdentificationType?.value]);

  const fields = useMemo(() => {
    if (selectedAge) {
      return <></>;
    }

    return (
      <Fragment>
        <Row className="pt-5">
          <Col xs={12}>
            <SectionTitle text={t(`${prefix}.title`)} />
          </Col>
        </Row>
        <Row>
          <Col className="pb-3" xs={12} lg={4}>
            <TextInput
              name={fieldNames.representativeLegal}
              label={t(`${prefix}.${fieldNames.representativeLegal}`)}
              control={control}
            />
          </Col>
          <Col className="pb-3" xs={12} lg={4}>
            <Select
              name={`${fieldNames.identificationType}`}
              label={t(`${prefix}.${fieldNames.identificationType}`)}
              options={identificationTypeOptions}
              isClearable={false}
              control={control}
            />
          </Col>

          {isPassport && (
            <Col className="pb-3" xs={12} lg={4}>
              <Select
                name={`${fieldNames.country}`}
                label={t(`${prefix}.${fieldNames.country}`)}
                options={countryOptions}
                isClearable={false}
                control={control}
                shouldUnregister={true}
              />
            </Col>
          )}

          <Col className="pb-3" xs={12} lg={4}>
            <TextInput
              name={fieldNames.identification}
              label={t(`${prefix}.${fieldNames.identification}`)}
              control={control}
            />
          </Col>
          <Col className="pb-3" xs={12} lg={4}>
            <Select
              name={`${fieldNames.parenthood}`}
              label={t(`${prefix}.${fieldNames.parenthood}`)}
              options={parenthoodOptions}
              isClearable={false}
              control={control}
            />
          </Col>
        </Row>
      </Fragment>
    );
  }, [
    fieldNames.representativeLegal,
    fieldNames.identificationType,
    fieldNames.country,
    fieldNames.identification,
    fieldNames.parenthood,
    t,
    selectedAge,
    isPassport,
    countryOptions,
    identificationTypeOptions,
    control,
    prefix,
  ]);

  return <>{fields}</>;
};

export default DynamicFamilyGroupDataReformada;
